/**
 * Smoke-test page (Phase 1).
 *  - Pings GET /api/smoke. In dev this hits the Vite proxy → local Hono Worker.
 *    In prod it hits VITE_API_BASE + /api/smoke (the deployed Worker URL).
 *  - Renders every design token swatch so the Tailwind config is visibly verified.
 *  - Renders all three font families.
 * Reach it at /smoke.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

// In dev (no env var set) this is "" so we hit "/api/smoke" through the Vite proxy.
// In prod, set VITE_API_BASE in .env to the deployed Worker URL, e.g.
//   VITE_API_BASE=https://iswis-api.your-subdomain.workers.dev
const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');

type SmokeResponse = {
  ok: boolean;
  service: string;
  phase: number;
  ts: string;
};

const SWATCHES: { name: string; bg: string; text?: string }[] = [
  { name: 'cream', bg: 'bg-cream', text: 'text-burgundy' },
  { name: 'cream-soft', bg: 'bg-cream-soft', text: 'text-burgundy' },
  { name: 'cream-bright', bg: 'bg-cream-bright', text: 'text-burgundy' },
  { name: 'cream-deep', bg: 'bg-cream-deep', text: 'text-burgundy' },
  { name: 'forest', bg: 'bg-forest', text: 'text-cream-bright' },
  { name: 'forest-soft', bg: 'bg-forest-soft', text: 'text-cream-bright' },
  { name: 'pink', bg: 'bg-pink', text: 'text-cream-bright' },
  { name: 'pink-deep', bg: 'bg-pink-deep', text: 'text-cream-bright' },
  { name: 'pink-light', bg: 'bg-pink-light', text: 'text-burgundy' },
  { name: 'burgundy', bg: 'bg-burgundy', text: 'text-cream-bright' },
  { name: 'burgundy-soft', bg: 'bg-burgundy-soft', text: 'text-cream-bright' },
  { name: 'burgundy-faint', bg: 'bg-burgundy-faint', text: 'text-cream-bright' },
  { name: 'ink', bg: 'bg-ink', text: 'text-cream-bright' },
  { name: 'teal', bg: 'bg-teal', text: 'text-cream-bright' },
  { name: 'orange', bg: 'bg-orange', text: 'text-cream-bright' },
  { name: 'purple', bg: 'bg-purple', text: 'text-cream-bright' },
  { name: 'danger', bg: 'bg-danger', text: 'text-cream-bright' },
];

export default function Smoke() {
  const [data, setData] = useState<SmokeResponse | null>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/smoke`)
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return (await r.json()) as SmokeResponse;
      })
      .then((json) => {
        if (!cancelled) setData(json);
      })
      .catch((err) => {
        if (!cancelled) setError(String(err.message ?? err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-full bg-cream-soft text-burgundy font-hanken p-8 max-w-[960px] mx-auto">
      <div className="flex items-baseline justify-between mb-8">
        <h1 className="font-bagel text-[36px] text-burgundy">Phase 1 smoke test</h1>
        <nav className="flex gap-3 text-[13px]">
          <Link to="/" className="text-pink-deep underline">
            Submitter form
          </Link>
          <Link to="/admin" className="text-pink-deep underline">
            Admin inbox
          </Link>
        </nav>
      </div>

      {/* Worker ping */}
      <section className="mb-10">
        <h2 className="font-bold text-[14px] uppercase tracking-[1.5px] text-burgundy-faint mb-3">
          Worker reachability
        </h2>
        <div className="bg-cream-bright border border-burgundy/20 rounded-xl p-5">
          {!data && !error && <p>Pinging /api/smoke…</p>}
          {error && (
            <div>
              <p className="text-danger font-bold mb-1">Couldn't reach /api/smoke.</p>
              <p className="text-burgundy-soft text-[13px] mb-2">{error}</p>
              <p className="text-burgundy-soft text-[13px]">
                Run the Worker in another terminal: <code>npm run worker:dev</code>.
                Vite proxies <code>/api</code> to <code>http://127.0.0.1:8787</code>.
              </p>
            </div>
          )}
          {data && (
            <pre className="whitespace-pre-wrap text-[13px] text-burgundy">
              {JSON.stringify(data, null, 2)}
            </pre>
          )}
        </div>
      </section>

      {/* Tokens */}
      <section className="mb-10">
        <h2 className="font-bold text-[14px] uppercase tracking-[1.5px] text-burgundy-faint mb-3">
          Design tokens (palette)
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {SWATCHES.map((s) => (
            <div
              key={s.name}
              className={`${s.bg} ${s.text ?? ''} rounded-lg p-4 h-24 flex flex-col justify-between border border-burgundy/10`}
            >
              <span className="text-[12px] font-bold uppercase tracking-[0.5px]">
                {s.name}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Fonts */}
      <section className="mb-10">
        <h2 className="font-bold text-[14px] uppercase tracking-[1.5px] text-burgundy-faint mb-3">
          Fonts
        </h2>
        <div className="bg-cream-bright border border-burgundy/20 rounded-xl p-6 space-y-4">
          <div className="font-rye text-[28px] text-burgundy">
            I SAID WHAT I SAID — Rye (wordmark only)
          </div>
          <div className="font-bagel text-[40px] text-burgundy">
            GOT A DILEMMA? — Bagel Fat One
          </div>
          <div className="font-hanken text-[16px] text-burgundy">
            Hanken Grotesk handles every other string in the product. Regular,{' '}
            <strong>bold</strong>, and{' '}
            <span className="font-semibold">semibold</span> all come through.
          </div>
        </div>
      </section>

      {/* Shadows */}
      <section className="mb-10">
        <h2 className="font-bold text-[14px] uppercase tracking-[1.5px] text-burgundy-faint mb-3">
          Chunky shadows (loud side)
        </h2>
        <div className="flex flex-wrap gap-6">
          {([2, 3, 5, 6, 7, 8] as const).map((n) => (
            <div
              key={n}
              className={`bg-cream-bright border-[3px] border-burgundy rounded-xl px-5 py-3 shadow-chunk-${n} font-bold`}
            >
              chunk-{n}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
